import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowClockwise, EnvelopeSimple, Phone, Buildings, Trash, CheckCircle } from "@phosphor-icons/react";

const STATUS = [
  { k: "all", l: "Todos" },
  { k: "nuevo", l: "Nuevos" },
  { k: "contactado", l: "Contactados" },
  { k: "convertido", l: "Convertidos" },
  { k: "descartado", l: "Descartados" },
];

const STATUS_STYLE = {
  nuevo: "bg-[#5E6AD2]/15 text-[#5E6AD2] border-[#5E6AD2]/30",
  contactado: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30",
  convertido: "bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/30",
  descartado: "bg-[#71717A]/15 text-[#71717A] border-[#71717A]/30",
};

export default function AdminLeads({ headers }) {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/leads", { headers, params });
      setLeads(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (lead, status) => {
    try {
      await api.patch(`/admin/leads/${lead.id}`, { status }, { headers });
      toast.success(`Marcado como ${status}`);
      load();
    } catch (err) { toast.error("Error"); }
  };

  const remove = async (lead) => {
    if (!window.confirm(`¿Eliminar lead de ${lead.email}?`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`, { headers });
      load();
    } catch (err) { toast.error("Error"); }
  };

  return (
    <section data-testid="admin-leads">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#71717A]">Formulario de contacto</div>
          <h2 className="font-display text-xl font-medium tracking-tight">
            Leads capturados ({leads.length})
          </h2>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          data-testid="leads-refresh"
          className="border-[#27272A] bg-[#121214] text-white hover:bg-[#1F1F23]"
        >
          <ArrowClockwise size={14} className="mr-1.5" /> Refrescar
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mb-5">
        {STATUS.map(s => (
          <button
            key={s.k}
            onClick={() => setFilter(s.k)}
            data-testid={`lead-filter-${s.k}`}
            className={`px-3 h-9 text-xs uppercase tracking-wider rounded-md border transition-colors ${
              filter === s.k
                ? "bg-[#5E6AD2] border-[#5E6AD2] text-white"
                : "bg-[#121214] border-[#27272A] text-[#A1A1AA] hover:text-white"
            }`}
          >{s.l}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#71717A]">Cargando...</div>
      ) : leads.length === 0 ? (
        <div className="bg-[#121214] border border-[#27272A] rounded-lg p-10 text-center text-sm text-[#71717A]">
          Sin leads en este filtro.
        </div>
      ) : (
        <div className="space-y-3">
          {leads.map(l => (
            <div
              key={l.id}
              data-testid={`lead-${l.email}`}
              className="bg-[#121214] border border-[#27272A] rounded-lg p-5"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium">{l.name}</span>
                    <Badge className={`text-[10px] ${STATUS_STYLE[l.status] || STATUS_STYLE.nuevo}`}>
                      {l.status}
                    </Badge>
                    {l.business_type && (
                      <span className="text-[10px] uppercase tracking-wider text-[#71717A]">
                        {l.business_type}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#A1A1AA] flex items-center gap-3 flex-wrap mb-2">
                    {l.business && <span className="flex items-center gap-1"><Buildings size={10} />{l.business}</span>}
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-white">
                      <EnvelopeSimple size={10} />{l.email}
                    </a>
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-white">
                        <Phone size={10} />{l.phone}
                      </a>
                    )}
                  </div>
                  {l.message && (
                    <blockquote className="mt-2 pl-3 border-l-2 border-[#5E6AD2]/40 text-xs text-[#D4D4D8] italic">
                      "{l.message}"
                    </blockquote>
                  )}
                  <div className="text-[10px] text-[#71717A] mt-2">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <select
                    value={l.status}
                    onChange={e => setStatus(l, e.target.value)}
                    data-testid={`lead-status-${l.email}`}
                    className="h-8 px-2 text-xs rounded-md bg-[#09090B] border border-[#27272A] text-white focus:outline-none"
                  >
                    <option value="nuevo">nuevo</option>
                    <option value="contactado">contactado</option>
                    <option value="convertido">convertido</option>
                    <option value="descartado">descartado</option>
                  </select>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => remove(l)}
                    data-testid={`lead-delete-${l.email}`}
                    className="h-8 border-[#27272A] bg-transparent hover:bg-[#1F1F23] text-[#EF4444]"
                  >
                    <Trash size={12} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
